import { useState } from 'react';
import { CheckCircle, XCircle, Edit2, Send, AlertTriangle, Sparkles } from 'lucide-react';
import Button from '../../../components/ui/Button';
import ToneAdjuster from './ToneAdjuster';
import BrandVoiceSelector from './BrandVoiceSelector';
import PersonalizationPanel from './PersonalizationPanel';

const ResponseReviewCard = ({ response, urgency, isSelected, onSelect, onApprove, onReject, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedResponse, setEditedResponse] = useState(response?.suggestedResponse || '');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCustomization, setShowCustomization] = useState(false);
  const [tone, setTone] = useState('professional');
  const [brandVoice, setBrandVoice] = useState('default');

  const getUrgencyColor = () => {
    const colors = {
      high: 'bg-red-100 text-red-800 border-red-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors?.[urgency] || colors?.low;
  };

  const getSafetyScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleApprove = async (autoPost = false) => {
    try {
      setLoading(true);
      await onApprove(response?.id, editedResponse, autoPost);
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason?.trim()) return;
    
    try {
      setLoading(true);
      await onReject(response?.id, rejectionReason);
      setShowRejectModal(false);
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      await onEdit(response?.id, editedResponse);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving edit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToneChange = (newTone) => {
    setTone(newTone);
    // In a real implementation, this would call an AI service to adjust the tone
    // For now, we'll just update the state
  };

  const handleBrandVoiceChange = (newVoice) => {
    setBrandVoice(newVoice);
    // In a real implementation, this would apply brand voice guidelines
  };

  const handlePersonalizationInsert = (token) => {
    setEditedResponse(prev => `${prev} ${token}`);
  };

  return (
    <div className={`bg-white rounded-lg border-2 shadow-sm transition-all ${
      isSelected ? 'border-blue-500 shadow-md' : 'border-gray-200'
    }`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3 flex-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onSelect}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900">{response?.comment?.commenterName}</h3>
                <span className="text-sm text-gray-500">@{response?.comment?.commenterHandle}</span>
                <span className={`text-xs px-2 py-1 rounded-full border ${getUrgencyColor()}`}>
                  {urgency?.toUpperCase()} URGENCY
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {response?.comment?.platform} • {response?.automationRuleName}
              </p>
            </div>
          </div>
          
          {/* Safety Score */}
          <div className="text-right">
            <p className="text-xs text-gray-500 mb-1">Safety Score</p>
            <p className={`text-lg font-bold ${getSafetyScoreColor(response?.safetyScore)}`}>
              {response?.safetyScore}/100
            </p>
          </div>
        </div>

        {/* Original Comment */}
        <div className="mb-3 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1 font-medium">Original Comment:</p>
          <p className="text-sm text-gray-800">{response?.comment?.commentText}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
              {response?.comment?.sentiment}
            </span>
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
              {response?.comment?.intent}
            </span>
            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
              {response?.comment?.responseOpportunity} opportunity
            </span>
          </div>
        </div>

        {/* Safety Flags */}
        {response?.safetyFlags?.length > 0 && (
          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-yellow-800 mb-1">Safety Warnings:</p>
                <ul className="text-xs text-yellow-700 space-y-0.5">
                  {response?.safetyFlags?.map((flag, index) => (
                    <li key={index}>• {flag}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* AI-Generated Response */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <p className="text-xs font-medium text-gray-700">AI-Generated Response:</p>
            </div>
            {response?.status === 'pending' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowCustomization(!showCustomization)}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Sparkles className="w-3 h-3" />
                  {showCustomization ? 'Hide' : 'Customize'}
                </button>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Edit2 className="w-3 h-3" />
                  {isEditing ? 'Cancel' : 'Edit'}
                </button>
              </div>
            )}
          </div>
          
          {/* Customization Controls */}
          {showCustomization && response?.status === 'pending' && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
              <ToneAdjuster currentTone={tone} onToneChange={handleToneChange} />
              <BrandVoiceSelector currentVoice={brandVoice} onVoiceChange={handleBrandVoiceChange} />
              <PersonalizationPanel onInsertToken={handlePersonalizationInsert} />
            </div>
          )}

          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editedResponse}
                onChange={(e) => setEditedResponse(e?.target?.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
              />
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleSaveEdit}
                  disabled={loading || !editedResponse?.trim()}
                  size="sm"
                >
                  Save Changes
                </Button>
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedResponse(response?.suggestedResponse);
                  }}
                  size="sm"
                  variant="ghost"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-800 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              {editedResponse}
            </p>
          )}
        </div>

        {/* Actions */}
        {response?.status === 'pending' && !isEditing && (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => handleApprove(false)}
              disabled={loading || !editedResponse?.trim()}
              size="sm"
              className="flex-1 flex items-center justify-center gap-1"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Approve
                </>
              )}
            </Button>
            
            <Button
              onClick={() => handleApprove(true)}
              disabled={loading || !editedResponse?.trim()}
              size="sm"
              variant="default"
              className="flex-1 flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700"
            >
              <Send className="w-4 h-4" />
              Approve & Post
            </Button>
            
            <Button
              onClick={() => setShowRejectModal(true)}
              disabled={loading}
              size="sm"
              variant="outline"
              className="flex items-center justify-center gap-1 border-red-300 text-red-600 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4" />
              Reject
            </Button>
          </div>
        )}

        {/* Approved/Rejected Status */}
        {response?.status === 'approved' && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs text-green-800">
              <strong>Approved</strong> {response?.autoPosted ? 'and posted' : ''} on {new Date(response?.approvedAt)?.toLocaleString()}
            </p>
          </div>
        )}

        {response?.status === 'rejected' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-800">
              <strong>Rejected</strong> on {new Date(response?.rejectedAt)?.toLocaleString()}
            </p>
            {response?.rejectionReason && (
              <p className="text-xs text-red-700 mt-1">Reason: {response?.rejectionReason}</p>
            )}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Response</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this response:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e?.target?.value)}
              placeholder="Enter rejection reason..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm mb-4"
            />
            <div className="flex items-center gap-2">
              <Button
                onClick={handleReject}
                disabled={loading || !rejectionReason?.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {loading ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
              <Button
                onClick={() => setShowRejectModal(false)}
                disabled={loading}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponseReviewCard;